import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import type { SUMMARY_ROUTE } from 'cpool-constants';
import { AGGREGATE_NAMES, LOAN_REQUEST_COMMANDS, LOANREQUEST_DEFAULT_STATUS, SUMMARY_ROUTES } from 'cpool-constants';
import type { SchemaLoanRequestResolver_GetSummary } from 'cpool-tables/backend-resolvers';
import { sleep } from 'cpool-utils';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import type { LeadListFilter } from '../schemas/route.schema';
import { APICommunicator } from '~/api/APICommunicator';
import { AssignedUsersToast } from '~/components/AssignedUsers/AssignedUsersToast';
import { invalidateAssignedUsers, isAssignmentConflict } from '~/components/AssignedUsers/assignmentConflict';
import { LEAD_LIST_DEFAULT_FILTERS, PAGINATION } from '~/constants';
import { LEAD_LIST_ACTION_FILTER } from '~/features/leadList/utils/actionFilters/leadListActionFilter';
import { buildLoanRequestGetSummarySearchParams } from '~/features/leadList/utils/buildLoanRequestGetSummarySearchParams';
import { setQueryPosition } from '~/store/queryPositionStore';
import { queryKeys } from '~/utils/queryKeys';

type AdviserMutationVariables = { adviserId: string; leadId: string; name: string };

export const useAdviserMutation = (currentLeadId?: string) => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const leadsMatch = useRouterState({
        select: (s) =>
            s.matches.find((m) => m.routeId === SUMMARY_ROUTES.LEADS || m.routeId === SUMMARY_ROUTES.BUCKET_LEADS) ??
            null,
    });
    const leadsDetailsMatch = useRouterState({
        select: (s) => s.matches.find((m) => m.routeId === '/_app/leads_/details/$leadId') ?? null,
    });

    const isLeadsRouteActive = !!leadsMatch;
    const search = (leadsMatch?.search as Partial<LeadListFilter>) ?? {};
    const {
        adviser = LEAD_LIST_DEFAULT_FILTERS.adviser,
        bank = LEAD_LIST_DEFAULT_FILTERS.bank,
        bucket = LEAD_LIST_DEFAULT_FILTERS.bucket,
        page = LEAD_LIST_DEFAULT_FILTERS.page,
        pageSize = PAGINATION.LEAD_LIST.DEFAULT_PAGE_SIZE,
        partnerLevel = LEAD_LIST_DEFAULT_FILTERS.partnerLevel,
        resubmission = LEAD_LIST_DEFAULT_FILTERS.resubmission,
        status = LOANREQUEST_DEFAULT_STATUS.ALL,
        subStatus = LEAD_LIST_DEFAULT_FILTERS.subStatus,
        tag = LEAD_LIST_DEFAULT_FILTERS.tag,
    } = search;

    const filterForSummary: Pick<
        LeadListFilter,
        | 'adviser'
        | 'bank'
        | 'bucket'
        | 'page'
        | 'pageSize'
        | 'partnerLevel'
        | 'resubmission'
        | 'status'
        | 'subStatus'
        | 'tag'
    > = {
        adviser,
        bank,
        bucket,
        page,
        pageSize,
        partnerLevel,
        resubmission,
        status,
        subStatus,
        tag,
    };

    const buildSummaryKeyArgs = (routeId?: SUMMARY_ROUTE) => {
        const {
            filters,
            limit,
            offset,
            permissionsActionFilter,
            route: summaryRoute,
            status: summaryStatus,
        } = buildLoanRequestGetSummarySearchParams({
            filter: filterForSummary,
            permissionsActionFilter: LEAD_LIST_ACTION_FILTER,
            route: routeId ?? SUMMARY_ROUTES.LEADS,
        });
        return {
            filters,
            limit,
            offset,
            permissionsActionFilter,
            route: summaryRoute,
            status: summaryStatus,
        };
    };

    const buildCountsKeyArgs = (routeId?: SUMMARY_ROUTE) => ({
        filters: buildLoanRequestGetSummarySearchParams({
            filter: filterForSummary,
            route: routeId ?? SUMMARY_ROUTES.LEADS,
        }).filters,
        route: routeId,
        status,
    });

    const summaryKeyArgs = buildSummaryKeyArgs(leadsMatch?.routeId);
    const countsKeyArgs = buildCountsKeyArgs(leadsMatch?.routeId);

    const getSummaryKey = queryKeys.getSummary(summaryKeyArgs);
    const getCountsKey = queryKeys.getCounts(countsKeyArgs);

    const adviserQueryMutate = useMutation({
        // name only feeds the success toast
        mutationFn: async ({ adviserId, leadId }: AdviserMutationVariables) => {
            return APICommunicator.sendCommand({
                aggregateId: leadId,
                aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                commandName: LOAN_REQUEST_COMMANDS.assignToUser,
                payload: {
                    userId: adviserId,
                },
            });
        },
        onError: (error: Error & { status?: number }, { leadId }, context) => {
            if (isAssignmentConflict(error)) {
                toast.error(t('LEADS.ASSIGNED_USERS.CHANGED_MEANWHILE'));
                invalidateAssignedUsers(queryClient, leadId);
            } else {
                toast.error(t('LEADS.ADVISER.ASSIGN_ERROR'));
            }
            if (!context) return;
            if (context.previousSummary) queryClient.setQueryData(context.summaryKey, context.previousSummary);
            // command may have been persisted despite the error (e.g. timeout)
            queryClient.invalidateQueries({ queryKey: context.summaryKey });
            queryClient.invalidateQueries({ queryKey: context.countsKey });
        },
        onMutate: async ({ adviserId, leadId }: AdviserMutationVariables) => {
            if (!isLeadsRouteActive) return undefined;

            // keys of the mutation's render; navigation can change them before onError runs
            const summaryKey = getSummaryKey;
            const countsKey = getCountsKey;
            queryClient.cancelQueries({ queryKey: summaryKey });
            const previousSummary = queryClient.getQueryData<SchemaLoanRequestResolver_GetSummary>(summaryKey);
            queryClient.setQueryData(summaryKey, (data: SchemaLoanRequestResolver_GetSummary | undefined) => {
                if (!data || !Array.isArray(data)) {
                    return data;
                }
                // copy instead of mutating, so previousSummary stays intact for the rollback
                return data.map((lead) =>
                    lead.id === leadId ? { ...lead, activeUserId: adviserId, userIds: [adviserId] } : lead,
                );
            });
            queryClient.cancelQueries({ queryKey: countsKey });
            queryClient.setQueryData(countsKey, (data: { ALL: number; [key: string]: number } | undefined) => {
                return data ? { ...data } : data;
            });
            return { countsKey, previousSummary, summaryKey };
        },
        onSettled: async (data, error, { leadId }) => {
            if (!data?.position || error) return;
            if (leadsDetailsMatch) {
                queryClient.invalidateQueries({ queryKey: queryKeys.get(leadsDetailsMatch.params.leadId) });
            }
            queryClient.invalidateQueries({
                queryKey: queryKeys.getAclPermissions({ loanRequestId: leadId }),
            });

            // TODO position wird aktualisiert aber das sorgt für einen reload der LeadListe bzw. Komponente
            // TODO Es spielt keine Rolle was am Ende die position setzt, sobald sie gesetzt wird, wird die LeadListe neu geladen und die Summary flackert
            // queryPosition.current = data.position;
            // TODO quick and dirty workaround
            await sleep(250);
            if (isLeadsRouteActive) {
                queryClient.invalidateQueries({ queryKey: getCountsKey });
                queryClient.invalidateQueries({ queryKey: getSummaryKey });
            }
            setQueryPosition(JSON.stringify(queryKeys.find()), data.position);
        },
        onSuccess: async ({ position }, { name }) => {
            toast.success(
                <AssignedUsersToast
                    body={t('LEADS.ASSIGNED_USERS.NOW_RESPONSIBLE', { name })}
                    title={t('LEADS.ASSIGNED_USERS.SINGLE_CHANGE_SUCCESS')}
                />,
            );
            if (currentLeadId) {
                setQueryPosition(JSON.stringify(queryKeys.duplicateLeads(currentLeadId)), position);
            }
        },
    });

    return { changeAdviser: adviserQueryMutate.mutateAsync, isPending: adviserQueryMutate.isPending };
};
